'use client';

import { ArrowUpIcon, LinkIcon } from '@heroicons/react/24/outline';
import { usePathname, useSearchParams } from 'next/navigation';
import { useCopyToClipboard } from 'react-use';

import { cn } from '@/lib/utils';

export default function PreviewNotification() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const storyblok = searchParams.get('_storyblok');

  const [copy, copyToClipboard] = useCopyToClipboard();

  return (
    <div
      className={cn(
        'group fixed bottom-0 left-1/2 z-[1000] mb-8 -translate-x-1/2',
        storyblok && 'opacity-50 hover:opacity-100 duration-500 ease-smooth'
      )}
    >
      <div className="absolute inset-0 rounded-full border border-[#0F7A77] bg-[#16a6a1]/80 outline outline-2 outline-white/20 backdrop-blur-lg duration-500 ease-smooth group-hover:scale-[1.025] group-hover:bg-[#16a6a1]/90"></div>
      <div className="relative z-10 flex items-center justify-center gap-0.5 p-0.5">
        <div className=" flex items-center justify-start gap-3 px-4 py-2 pr-0 text-white">
          <svg
            className="size-4 pointer-events-none w-4 h-4"
            viewBox="0 0 23 26"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M7.73242 11.9072H12.8066C12.9512 11.9072 13.0781 11.9424 13.1934 12.0073C13.3105 12.0732 13.416 12.1694 13.5146 12.2905C13.6963 12.542 13.79 12.8447 13.7832 13.1528C13.7832 13.5122 13.6611 13.7998 13.4658 13.9912C13.3574 14.0854 13.2383 14.1626 13.1094 14.2163C12.9756 14.272 12.833 14.3027 12.6846 14.3027H7.73242V11.9072Z"
              fill="currentColor"
            />
            <path
              d="M7.73242 7.11572H12.3926C12.5469 7.11572 12.6924 7.15479 12.8213 7.22021C12.8955 7.25732 12.9639 7.30322 13.0264 7.35547C13.1973 7.52295 13.2949 7.78662 13.2949 8.146C13.2949 8.55322 13.1973 8.86475 12.9775 9.0083C12.7822 9.15186 12.5381 9.27197 12.2949 9.27197H7.73242V7.11572Z"
              fill="currentColor"
            />
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M0 1.11523C0 0.506836 0.506836 0 1.14062 0H21.0293C21.6631 0 22.0176 0.532227 22.0176 1.14014V21.0332C22.0176 21.6665 21.6377 22.0469 21.0293 22.0469H7.70215L4.05371 26V22.0215H1.11523C0.819336 22.0215 0.547852 21.9316 0.34668 21.7695C0.133789 21.5981 0 21.3457 0 21.0332V1.11523ZM15.6035 4.31006C15.1982 4.08203 14.7676 3.98096 14.2607 3.98096H4.08203V18.1626H13.0459C13.7295 18.1626 14.3877 18.0361 14.9951 17.8843C15.6035 17.7578 16.1348 17.479 16.6162 17.1753C17.0713 16.8965 17.4512 16.5166 17.7051 16.061C17.7773 15.9341 17.8379 15.7939 17.8877 15.6436C18.0029 15.2925 18.0596 14.8833 18.0596 14.4399C18.0596 13.4526 17.8311 12.6675 17.376 12.0596C16.8945 11.4771 16.2617 11.0718 15.4512 10.8438C16.0947 10.5688 16.6289 10.0879 16.9707 9.47656C17.2998 8.86865 17.4766 8.23535 17.4766 7.57715C17.4883 6.68262 17.1836 5.8125 16.6162 5.12061C16.3379 4.7915 15.9834 4.5127 15.6035 4.31006Z"
              fill="currentColor"
            />
          </svg>

          <p
            className={cn(
              'min-w-24 select-none text-[15px]/none font-medium',
              storyblok && 'text-center'
            )}
          >
            {copy.value ? (
              'URL Copied'
            ) : (
              <>{storyblok ? 'Live Editing' : 'Preview Draft'}</>
            )}
          </p>
          <a
            href={`https://preview.mcel.ca${pathname}`}
            className="rounded-full bg-white/20 p-2"
            target="_blank"
            rel="noreferrer"
          >
            <ArrowUpIcon width={16} height={16} />
          </a>

          <button
            className="rounded-full bg-white/20 p-2"
            onClick={() =>
              copyToClipboard(`https://preview.mcel.ca${pathname}`)
            }
          >
            <LinkIcon width={16} height={16} />
          </button>
          <a
            aria-label="Exit Preview"
            href={`/api/exit-preview?slug=${pathname}`}
            className="w-10 h-10 block p-2 pointer-events-auto rounded-full bg-white/0 text-white duration-500 ease-smooth hover:bg-white/20 hover:duration-100"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
            </svg>
          </a>
        </div>
      </div>
    </div>
  );
}
